import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { auth, database, db } from '../firebase';
import { ref, get } from 'firebase/database';
import { doc, getDoc } from 'firebase/firestore';
import indexedDBService from '../services/indexedDBService';
import { AREAS_TRANSVERSALES, MODULOS_CENEVAL, CARRERAS_POR_UNIVERSIDAD } from '../constants/universitiesData';

const SCREEN_CATALOG = [
  { id: 'home', name: 'Inicio (Home)', path: '/dashboard', loader: () => import('./Home') },
  { id: 'simulador', name: 'Simulador EXANI-II', path: '/dashboard/simulador', loader: () => import('./SimuladorExamen') },
  { id: 'uninav', name: 'Batería UNINAV 2026', path: '/dashboard/uninav-evaluacion', loader: () => import('./UninavAssessment') },
  { id: 'quiz', name: 'Selección de Quizzes', path: '/dashboard/quiz', loader: () => import('./QuizSelection') },
  { id: 'quiz-view', name: 'Motor de Quiz Interactivo', path: '/dashboard/quiz/demo', loader: () => import('./Quiz') },
  { id: 'quiz-results', name: 'Resultados de Quiz', path: '/dashboard/quiz-results', loader: () => import('./QuizResults') },
  { id: 'quiz-generator', name: 'Generador de Exámenes IA', path: '/dashboard/quiz-generator', loader: () => import('./QuizGenerator') },
  { id: 'material', name: 'Material de Estudio', path: '/dashboard/material', loader: () => import('./MaterialEstudio') },
  { id: 'documentos-ceneval', name: 'Documentos CENEVAL', path: '/dashboard/documentos-ceneval', loader: () => import('./DocumentosCeneval') },
  { id: 'bibliografia', name: 'Bibliografía Oficial', path: '/dashboard/bibliografia', loader: () => import('./Bibliografia') },
  { id: 'ia-vortex', name: 'Tutor IA Vortex', path: '/dashboard/ia-vortex', loader: () => import('./IAVortex') },
  { id: 'voz-vortex', name: 'Voz Vortex (Audio IA)', path: '/dashboard/voz-vortex', loader: () => import('./VozVortex') },
  { id: 'comunidad', name: 'Comunidad de Estudiantes', path: '/dashboard/comunidad', loader: () => import('./Comunidad') },
  { id: 'perfil', name: 'Perfil del Estudiante', path: '/dashboard/perfil', loader: () => import('./Profile') },
  { id: 'notificaciones', name: 'Centro de Notificaciones', path: '/dashboard/notificaciones', loader: () => import('./NotificationCenter') },
  { id: 'plan-estudio', name: 'Plan de Estudio Modular', path: '/dashboard/plan-estudio', loader: () => import('./PlanEstudioMenu') },
  { id: 'audios', name: 'Biblioteca de Audios', path: '/dashboard/audios', loader: () => import('./AudioLibrary') },
  { id: 'flashcards', name: 'Flashcards de Repaso', path: '/dashboard/flashcards', loader: () => import('./Flashcards') },
  { id: 'mapa-debilidades', name: 'Mapa de Calor de Debilidades', path: '/dashboard/mapa-debilidades', loader: () => import('./MapaCalorDebilidades') },
  { id: 'modo-guardia', name: 'Modo Guardia (Estudio Intensivo)', path: '/dashboard/modo-guardia', loader: () => import('./ModoGuardia') },
  { id: 'tutorials', name: 'Tutoriales y Guías', path: '/dashboard/tutorials', loader: () => import('./Tutorials') },
  { id: 'study-progress', name: 'Progreso y Métricas', path: '/dashboard/study-progress', loader: () => import('./StudyProgress') },
  { id: 'chat', name: 'Chat con Asesores', path: '/dashboard/chat', loader: () => import('./ChatList') },
  { id: 'modulos-exani', name: 'Banco de Módulos EXANI-II', path: '/dashboard/modulos-exani', loader: () => import('./BancoModulosExani') },
  { id: 'vault', name: 'Bóveda de Recursos', path: '/dashboard/vault', loader: () => import('./Vault') },
  { id: 'doc-viewer', name: 'Visor de Documentos', path: '/dashboard/doc-viewer', loader: () => import('./DocViewer') },
  { id: 'configuracion', name: 'Configuración y Preferencias', path: '/dashboard/configuracion', loader: () => import('./Settings') },
  { id: 'test-results', name: 'Historial de Exámenes', path: '/dashboard/test-results', loader: () => import('./TestResults') }
];

const HealthCheck = () => {
  const navigate = useNavigate();
  const { user, profileData, isPremium } = useAuth();

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const [results, setResults] = useState({
    auth: { status: 'pending', name: 'Firebase Auth', latency: 0, details: '' },
    rtdb: { status: 'pending', name: 'Realtime Database', latency: 0, details: '' },
    firestore: { status: 'pending', name: 'Cloud Firestore', latency: 0, details: '' },
    indexedDb: { status: 'pending', name: 'IndexedDB Local Cache', latency: 0, details: '' },
    exaniData: { status: 'pending', name: 'Catálogo EXANI-II', latency: 0, details: '' },
    uninavEngine: { status: 'pending', name: 'Motor UNINAV 2026', latency: 0, details: '' },
    screens: []
  });

  const runDiagnostics = useCallback(async () => {
    setRunning(true);
    setProgress(5);

    const newResults = {
      auth: { status: 'running', name: 'Firebase Auth', latency: 0, details: 'Verificando sesión...' },
      rtdb: { status: 'pending', name: 'Realtime Database', latency: 0, details: '' },
      firestore: { status: 'pending', name: 'Cloud Firestore', latency: 0, details: '' },
      indexedDb: { status: 'pending', name: 'IndexedDB Local Cache', latency: 0, details: '' },
      exaniData: { status: 'pending', name: 'Catálogo EXANI-II', latency: 0, details: '' },
      uninavEngine: { status: 'pending', name: 'Motor UNINAV 2026', latency: 0, details: '' },
      screens: []
    };
    setResults({ ...newResults });

    // 1. Test Auth
    const t0Auth = performance.now();
    try {
      if (user && user.uid) {
        const token = await user.getIdToken(false);
        const latAuth = Math.round(performance.now() - t0Auth);
        newResults.auth = {
          status: 'ok',
          name: 'Firebase Auth',
          latency: latAuth,
          details: `Usuario: ${user.email || user.uid} (Licencia Premium: ${isPremium ? 'Activa ✅' : 'Inactiva ⚠️'})`
        };
      } else {
        newResults.auth = {
          status: 'warning',
          name: 'Firebase Auth',
          latency: 0,
          details: 'Sin sesión activa de usuario'
        };
      }
    } catch (e) {
      newResults.auth = {
        status: 'error',
        name: 'Firebase Auth',
        latency: Math.round(performance.now() - t0Auth),
        details: `Error al verificar token: ${e.message}`
      };
    }
    setResults(prev => ({ ...prev, auth: newResults.auth }));
    setProgress(20);

    // 2. Test Realtime Database
    const t0Rtdb = performance.now();
    try {
      const pingRef = ref(database, 'config/cursoPeriodoActual');
      const snap = await get(pingRef);
      const latRtdb = Math.round(performance.now() - t0Rtdb);
      newResults.rtdb = {
        status: 'ok',
        name: 'Realtime Database',
        latency: latRtdb,
        details: `Conexión exitosa (${latRtdb} ms). Nodo consultado: ${snap.exists() ? snap.val() : 'Nodo accesible'}`
      };
    } catch (e) {
      newResults.rtdb = {
        status: 'error',
        name: 'Realtime Database',
        latency: Math.round(performance.now() - t0Rtdb),
        details: `Fallo de lectura RTDB: ${e.message}`
      };
    }
    setResults(prev => ({ ...prev, rtdb: newResults.rtdb }));
    setProgress(35);

    // 3. Test Cloud Firestore
    const t0Fs = performance.now();
    try {
      if (user?.uid) {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        const latFs = Math.round(performance.now() - t0Fs);
        newResults.firestore = {
          status: 'ok',
          name: 'Cloud Firestore',
          latency: latFs,
          details: `Perfil cargado (${latFs} ms). Carrera configurada: ${snap.exists() ? (snap.data().carreraNombre || snap.data().carrera || 'No seleccionada') : 'Doc no existe'}`
        };
      } else {
        newResults.firestore = {
          status: 'warning',
          name: 'Cloud Firestore',
          latency: 0,
          details: 'No se pudo probar Firestore sin sesión de usuario'
        };
      }
    } catch (e) {
      newResults.firestore = {
        status: 'error',
        name: 'Cloud Firestore',
        latency: Math.round(performance.now() - t0Fs),
        details: `Fallo Firestore: ${e.message}`
      };
    }
    setResults(prev => ({ ...prev, firestore: newResults.firestore }));
    setProgress(50);

    // 4. Test IndexedDB
    const t0Idb = performance.now();
    try {
      await indexedDBService.init();
      const areas = await indexedDBService.getAreas();
      const latIdb = Math.round(performance.now() - t0Idb);
      newResults.indexedDb = {
        status: 'ok',
        name: 'IndexedDB Local Cache',
        latency: latIdb,
        details: `IndexedDB activo (${latIdb} ms). Áreas en caché local: ${areas?.length || 0}`
      };
    } catch (e) {
      newResults.indexedDb = {
        status: 'warning',
        name: 'IndexedDB Local Cache',
        latency: Math.round(performance.now() - t0Idb),
        details: `IndexedDB no disponible o en modo incógnito: ${e.message}`
      };
    }
    setResults(prev => ({ ...prev, indexedDb: newResults.indexedDb }));
    setProgress(65);

    // 5. Test EXANI Data Integrity
    try {
      const countTransversales = AREAS_TRANSVERSALES?.length || 0;
      const countModulos = Object.keys(MODULOS_CENEVAL || {}).length;
      const countCarrerasUninav = CARRERAS_POR_UNIVERSIDAD?.UNINAV?.length || 0;
      newResults.exaniData = {
        status: countTransversales === 4 && countModulos >= 18 ? 'ok' : 'warning',
        name: 'Catálogo EXANI-II',
        latency: 1,
        details: `4 Áreas Transversales: ${countTransversales}/4 | Módulos Ceneval: ${countModulos} módulos | Carreras UNINAV: ${countCarrerasUninav}`
      };
    } catch (e) {
      newResults.exaniData = {
        status: 'error',
        name: 'Catálogo EXANI-II',
        latency: 0,
        details: `Error al validar catálogo: ${e.message}`
      };
    }
    setResults(prev => ({ ...prev, exaniData: newResults.exaniData }));
    setProgress(75);

    // 6. Test UNINAV Assessment Data
    try {
      const uninavModule = await import('../data/uninavAssessmentData');
      const psicometricoCount = (uninavModule.PSICOMETRICO_QUESTIONS || uninavModule.UNINAV_PSICOMETRICO_QUESTIONS)?.length || 0;
      const mmpiCount = (uninavModule.PERSONALITY_INVENTORY || uninavModule.UNINAV_PERSONALIDAD_QUESTIONS)?.length || 0;
      const mossCount = (uninavModule.MOSS_SITUATIONAL_QUESTIONS || uninavModule.UNINAV_MOSS_QUESTIONS)?.length || 0;
      const totalUninav = psicometricoCount + mmpiCount + mossCount;
      newResults.uninavEngine = {
        status: totalUninav >= 200 ? 'ok' : 'warning',
        name: 'Batería UNINAV 2026',
        latency: 2,
        details: `Total: ${totalUninav} reactivos oficiales | Personalidad/MMPI: ${mmpiCount} | Psicométrico: ${psicometricoCount} | MOSS Liderazgo: ${mossCount}`
      };
    } catch (e) {
      newResults.uninavEngine = {
        status: 'error',
        name: 'Batería UNINAV 2026',
        latency: 0,
        details: `Error cargando banco UNINAV: ${e.message}`
      };
    }
    setResults(prev => ({ ...prev, uninavEngine: newResults.uninavEngine }));
    setProgress(85);

    // 7. Test All 28 Screen Components (Dynamic Load Test)
    const screenResults = [];
    for (let i = 0; i < SCREEN_CATALOG.length; i++) {
      const item = SCREEN_CATALOG[i];
      const t0Screen = performance.now();
      try {
        const mod = await item.loader();
        const lat = Math.round(performance.now() - t0Screen);
        const hasExport = Boolean(mod.default || mod);
        screenResults.push({
          id: item.id,
          name: item.name,
          path: item.path,
          status: hasExport ? 'ok' : 'warning',
          latency: lat,
          details: hasExport ? `Módulo compilado correctamente (${lat} ms)` : 'No se encontró export default'
        });
      } catch (err) {
        screenResults.push({
          id: item.id,
          name: item.name,
          path: item.path,
          status: 'error',
          latency: Math.round(performance.now() - t0Screen),
          details: `Error al cargar: ${err.message}`
        });
      }
      setProgress(85 + Math.round(((i + 1) / SCREEN_CATALOG.length) * 15));
    }

    newResults.screens = screenResults;
    setResults({ ...newResults });
    setProgress(100);
    setRunning(false);
  }, [user, isPremium]);

  useEffect(() => {
    runDiagnostics();
  }, [runDiagnostics]);

  // Contadores
  const totalScreens = results.screens.length;
  const okScreens = results.screens.filter(s => s.status === 'ok').length;
  const warnScreens = results.screens.filter(s => s.status === 'warning').length;
  const errScreens = results.screens.filter(s => s.status === 'error').length;

  const coreTests = [results.auth, results.rtdb, results.firestore, results.indexedDb, results.exaniData, results.uninavEngine];
  const coreOk = coreTests.filter(t => t.status === 'ok').length;
  const totalCore = coreTests.length;

  const overallScore = totalScreens > 0
    ? Math.round(((okScreens + coreOk) / (totalScreens + totalCore)) * 100)
    : 100;

  const handleCopyReport = () => {
    const report = {
      timestamp: new Date().toISOString(),
      overallScore: `${overallScore}%`,
      coreServices: coreTests.map(t => ({ name: t.name, status: t.status, latency: `${t.latency}ms`, details: t.details })),
      screensSummary: { total: totalScreens, ok: okScreens, warnings: warnScreens, errors: errScreens },
      screens: results.screens.map(s => ({ name: s.name, path: s.path, status: s.status, latency: `${s.latency}ms`, details: s.details }))
    };
    navigator.clipboard.writeText(JSON.stringify(report, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const filteredScreens = results.screens.filter(s => {
    if (filterStatus === 'all') return true;
    return s.status === filterStatus;
  });

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1.5rem',
      color: 'var(--text-primary, #f8fafc)',
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Encabezado */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '2rem',
        background: 'linear-gradient(135deg, rgba(10, 26, 42, 0.85) 0%, rgba(13, 32, 53, 0.85) 100%)',
        border: '1px solid var(--tech-border, rgba(197, 160, 89, 0.25))',
        borderRadius: '16px',
        padding: '1.5rem 2rem',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.4)'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '1.8rem' }}>🛡️</span>
            <h1 style={{
              margin: 0,
              fontSize: '1.5rem',
              color: 'var(--gold-matte, #c5a059)',
              fontFamily: "'Orbitron', sans-serif",
              letterSpacing: '1.5px',
              textTransform: 'uppercase'
            }}>
              Vortex Health Check
            </h1>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary, #94a3b8)', fontSize: '0.88rem' }}>
            Auditoría en tiempo real de infraestructura, bases de datos y las 28 pantallas del Dashboard.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={runDiagnostics}
            disabled={running}
            style={{
              background: running ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 18px',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: running ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {running ? '⏳ Diagnosticando...' : '🔄 Re-ejecutar Test'}
          </button>

          <button
            onClick={handleCopyReport}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: copied ? '#4ade80' : 'var(--text-primary, #e2e8f0)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            {copied ? '✓ JSON Copiado' : '📋 Copiar Reporte'}
          </button>
        </div>
      </div>

      {/* Barra de progreso si está corriendo */}
      {running && (
        <div style={{
          width: '100%',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          height: '8px',
          overflow: 'hidden',
          marginBottom: '2rem',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #38bdf8 0%, #4ade80 100%)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      )}

      {/* Indicadores Clave de Salud */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgba(10, 26, 42, 0.65)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: '1.25rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '1px', marginBottom: '0.5rem' }}>
            Salud Global
          </div>
          <div style={{
            fontSize: '2.2rem',
            fontWeight: 800,
            fontFamily: "'Orbitron', sans-serif",
            color: overallScore >= 90 ? '#4ade80' : overallScore >= 70 ? '#facc15' : '#f87171'
          }}>
            {overallScore}%
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
            {overallScore >= 95 ? 'Todo el sistema operativo' : 'Se encontraron advertencias'}
          </div>
        </div>

        <div style={{
          background: 'rgba(10, 26, 42, 0.65)',
          border: '1px solid rgba(74, 222, 128, 0.25)',
          borderRadius: '12px',
          padding: '1.25rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#4ade80', letterSpacing: '1px', marginBottom: '0.5rem' }}>
            Pantallas Operativas
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: 800, color: '#4ade80' }}>
            {okScreens} <span style={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 400 }}>/ {totalScreens}</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
            100% libres de error de sintaxis
          </div>
        </div>

        <div style={{
          background: 'rgba(10, 26, 42, 0.65)',
          border: '1px solid rgba(248, 113, 113, 0.25)',
          borderRadius: '12px',
          padding: '1.25rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: errScreens > 0 ? '#f87171' : '#94a3b8', letterSpacing: '1px', marginBottom: '0.5rem' }}>
            Errores Críticos
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: 800, color: errScreens > 0 ? '#f87171' : '#94a3b8' }}>
            {errScreens}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
            {errScreens === 0 ? 'Sin fallos de compilación' : 'Requieren revisión'}
          </div>
        </div>

        <div style={{
          background: 'rgba(10, 26, 42, 0.65)',
          border: '1px solid rgba(250, 204, 21, 0.25)',
          borderRadius: '12px',
          padding: '1.25rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#facc15', letterSpacing: '1px', marginBottom: '0.5rem' }}>
            Servicios Base
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: 800, color: '#facc15' }}>
            {coreOk} <span style={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 400 }}>/ {totalCore}</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
            Auth, RTDB, Firestore, Cache
          </div>
        </div>
      </div>

      {/* Sección 1: Infraestructura y Datos */}
      <h2 style={{ fontSize: '1.1rem', color: 'var(--gold-matte, #c5a059)', letterSpacing: '1px', marginBottom: '1rem' }}>
        ⚙️ Infraestructura y Servicios de Datos
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1rem',
        marginBottom: '2.5rem'
      }}>
        {coreTests.map((t, idx) => (
          <div key={idx} style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: `1px solid ${t.status === 'ok' ? 'rgba(74, 222, 128, 0.25)' : t.status === 'error' ? 'rgba(248, 113, 113, 0.3)' : 'rgba(250, 204, 21, 0.25)'}`,
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <strong style={{ fontSize: '0.95rem' }}>{t.name}</strong>
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '12px',
                background: t.status === 'ok' ? 'rgba(74, 222, 128, 0.15)' : t.status === 'error' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(250, 204, 21, 0.15)',
                color: t.status === 'ok' ? '#4ade80' : t.status === 'error' ? '#f87171' : '#facc15'
              }}>
                {t.status === 'ok' ? '✓ OPERATIVO' : t.status === 'error' ? '✗ ERROR' : '⚠ ADVERTENCIA'}
              </span>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.4 }}>
              {t.details || 'Verificando...'}
            </p>
            {t.latency > 0 && (
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                Latencia: <span style={{ color: '#38bdf8' }}>{t.latency} ms</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Sección 2: Auditoría de Pantallas del Dashboard */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem'
      }}>
        <h2 style={{ fontSize: '1.1rem', color: 'var(--gold-matte, #c5a059)', letterSpacing: '1px', margin: 0 }}>
          🖥️ Auditoría de Pantallas del Dashboard ({filteredScreens.length})
        </h2>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { id: 'all', label: `Todas (${totalScreens})` },
            { id: 'ok', label: `Operativas (${okScreens})` },
            { id: 'error', label: `Errores (${errScreens})` }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilterStatus(f.id)}
              style={{
                background: filterStatus === f.id ? 'rgba(197, 160, 89, 0.2)' : 'rgba(255, 255, 255, 0.04)',
                border: filterStatus === f.id ? '1px solid #c5a059' : '1px solid rgba(255, 255, 255, 0.08)',
                color: filterStatus === f.id ? '#c5a059' : '#94a3b8',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '0.78rem',
                cursor: 'pointer'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
        gap: '0.75rem'
      }}>
        {filteredScreens.map((screen) => (
          <div
            key={screen.id}
            style={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: `1px solid ${screen.status === 'ok' ? 'rgba(74, 222, 128, 0.18)' : 'rgba(248, 113, 113, 0.3)'}`,
              borderRadius: '10px',
              padding: '0.85rem 1rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'transform 0.15s, border-color 0.15s'
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#f8fafc' }}>
                  {screen.name}
                </span>
                <span style={{
                  fontSize: '0.68rem',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  background: screen.status === 'ok' ? 'rgba(74, 222, 128, 0.12)' : 'rgba(248, 113, 113, 0.15)',
                  color: screen.status === 'ok' ? '#4ade80' : '#f87171'
                }}>
                  {screen.status === 'ok' ? '✓ OK' : '✗ ERROR'}
                </span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontFamily: 'monospace', marginBottom: '0.4rem' }}>
                {screen.path}
              </div>
              <div style={{ fontSize: '0.75rem', color: screen.status === 'ok' ? '#94a3b8' : '#fca5a5' }}>
                {screen.details}
              </div>
            </div>

            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => navigate(screen.path)}
                style={{
                  background: 'rgba(56, 189, 248, 0.1)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.25)',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Abrir pantalla ↗
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HealthCheck;
